import React from 'react';
import { Briefcase, Users, Shield, Edit, Trash2, Copy, Crown, BookOpen, ShieldCheck } from 'lucide-react';

// Category badge colors
const CATEGORY_COLORS = {
    'General': 'bg-slate-100 text-slate-600',
    'Management': 'bg-blue-100 text-blue-600',
    'HR': 'bg-purple-100 text-purple-600',
    'Executive': 'bg-amber-100 text-amber-600',
    'Training': 'bg-emerald-100 text-emerald-600',
    'Compliance': 'bg-rose-100 text-rose-600',
};

const RoleCard = ({ role, employeeCount, onEdit, onDelete, onDuplicate, isSystemRole }) => {
    // Calculate permission count safely
    const permissionCount = role.permissions ? Object.keys(role.permissions).length : 0;

    // Determine category from role metadata (if from template) or infer
    const category = role.category || null;
    const categoryColor = CATEGORY_COLORS[category] || 'bg-muted text-muted-foreground';

    // Count scoped permissions by type for a summary
    const scopeSummary = role.permissions ? Object.values(role.permissions).reduce((acc, val) => {
        if (val === 'all') acc.all++;
        else if (val === 'team') acc.team++;
        else if (val === 'own') acc.own++;
        else if (val === true) acc.simple++;
        return acc;
    }, { all: 0, team: 0, own: 0, simple: 0 }) : { all: 0, team: 0, own: 0, simple: 0 };

    return (
        <div className="bg-card rounded-xl shadow-sm border border-border p-5 hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-start mb-4">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                    <Briefcase size={24} />
                </div>
                <div className="flex gap-1">
                    <button
                        onClick={() => onDuplicate(role)}
                        className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-blue-500 transition-colors opacity-0 group-hover:opacity-100"
                        title="Duplicate Role"
                    >
                        <Copy size={16} />
                    </button>
                    <button
                        onClick={() => onEdit(role)}
                        className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-primary transition-colors"
                        title="Edit Role"
                    >
                        <Edit size={16} />
                    </button>
                    {!isSystemRole && (
                        <button
                            onClick={() => onDelete(role)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete Role"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>

            <h3 className="text-lg font-bold text-foreground mb-1">{role.role_name}</h3>
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2 h-10">
                {role.description || "No description provided."}
            </p>

            {/* Scope breakdown badges */}
            {permissionCount > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                    {scopeSummary.all > 0 && (
                        <span className="text-[10px] font-medium bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                            {scopeSummary.all} All
                        </span>
                    )}
                    {scopeSummary.team > 0 && (
                        <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                            {scopeSummary.team} Team
                        </span>
                    )}
                    {scopeSummary.own > 0 && (
                        <span className="text-[10px] font-medium bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">
                            {scopeSummary.own} Own
                        </span>
                    )}
                    {scopeSummary.simple > 0 && (
                        <span className="text-[10px] font-medium bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                            {scopeSummary.simple} Toggle
                        </span>
                    )}
                </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users size={16} className="text-muted-foreground" />
                    <span>{employeeCount} Employee{employeeCount !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Shield size={16} className="text-muted-foreground" />
                    <span>{permissionCount} Permission{permissionCount !== 1 ? 's' : ''}</span>
                </div>
            </div>

            {isSystemRole && (
                <div className="mt-3 text-xs text-center py-1 bg-muted text-muted-foreground rounded">
                    System Default
                </div>
            )}
        </div>
    );
};

export default RoleCard;
